/**
 * Pure helpers for auto-selecting a field when the user is on the
 * Field Details tab in the Farm View.
 *
 * Goal: single-field users (and anyone landing on the Field Details tab
 * without a valid selection) should immediately see the water-potential
 * graph instead of the empty "select a field" placeholder. The same
 * selection also drives the map highlight (both read `selectedFieldId`).
 *
 * Why not just fire on the tab transition: the field list loads
 * asynchronously and the selection can go stale when the user switches
 * distribution / farming unit. So the decision is based on whether the
 * current selection is valid for the fields on screen — not on a one-shot
 * transition — while a `suppressed` flag preserves an intentional deselect.
 */

const DETAILS_TAB = 'details';

/**
 * Is the current selection a real field in the on-screen list?
 * A stale id left over from a previous farming unit counts as invalid.
 *
 * @param {string|null|undefined} selectedFieldId
 * @param {Array<{id: string}>} fields - current unit's sorted fields
 * @returns {boolean}
 */
const hasValidFieldSelection = (selectedFieldId, fields) => {
  if (selectedFieldId === null || selectedFieldId === undefined) {
    return false;
  }
  return (fields || []).some((field) => field && field.id === selectedFieldId);
};

/**
 * Where does auto-select apply? Two cases keep a field selected:
 *  - Single-field users (`fieldCount === 1`) on ANY trailing tab, so their
 *    only field is always highlighted on the map and shown in the graph.
 *  - Any user on the Field Details tab, so the graph is shown instead of the
 *    empty "select a field" placeholder. On the Farm map tab with multiple
 *    fields we do NOT auto-select — that would hijack the overview map.
 *
 * @param {Object} params
 * @param {string} params.trailingTab - active trailing-panel tab
 * @param {number} params.fieldCount - number of selectable fields on screen
 * @returns {boolean}
 */
const autoSelectAppliesHere = ({ trailingTab, fieldCount }) =>
  fieldCount > 0 && (trailingTab === DETAILS_TAB || fieldCount === 1);

/**
 * Decide whether to auto-select the first field when nothing valid is picked.
 *
 * @param {Object} params
 * @param {string} params.trailingTab - active trailing-panel tab
 * @param {boolean} params.suppressed - true when the user just deselected
 *   on this tab/unit and we must not re-select until the context refreshes
 * @param {boolean} params.hasValidSelection - result of hasValidFieldSelection
 * @param {number} params.fieldCount - number of selectable fields on screen
 * @param {boolean} [params.focusPending] - a search / field-link focus is
 *   waiting for its farming unit; it picks the field, so auto-select waits
 * @returns {boolean} true when the first field should be auto-selected
 */
const shouldAutoSelectFirstField = ({
  trailingTab,
  suppressed,
  hasValidSelection,
  fieldCount,
  focusPending = false,
}) =>
  !suppressed &&
  !focusPending &&
  !hasValidSelection &&
  autoSelectAppliesHere({ trailingTab, fieldCount });

/**
 * Decide whether an existing AUTO-selection should move to the current
 * sorted-first field. The initial auto-select can land on the wrong field
 * because the data used for sorting (availability, performance, last
 * irrigation) streams in after the first pick — so an auto-selection follows
 * the sorted-first until the user manually picks a field.
 *
 * @param {Object} params
 * @param {string} params.trailingTab
 * @param {boolean} params.suppressed
 * @param {boolean} params.hasValidSelection
 * @param {boolean} params.wasAutoSelected - selection came from auto-select
 * @param {boolean} params.isFirstFieldSelected - selection is already the top
 * @param {number} params.fieldCount
 * @param {boolean} [params.focusPending] - see shouldAutoSelectFirstField
 * @returns {boolean} true when the auto-selection should re-align to the top
 */
const shouldRealignAutoSelection = ({
  trailingTab,
  suppressed,
  hasValidSelection,
  wasAutoSelected,
  isFirstFieldSelected,
  fieldCount,
  focusPending = false,
}) =>
  !suppressed &&
  !focusPending &&
  hasValidSelection &&
  wasAutoSelected &&
  !isFirstFieldSelected &&
  autoSelectAppliesHere({ trailingTab, fieldCount });

/**
 * A "fresh context" (entering the Details tab or switching farming unit)
 * clears any earlier intentional deselect, so auto-select can run again.
 *
 * @param {Object} params
 * @param {boolean} params.enteredDetailsTab
 * @param {boolean} params.farmingUnitChanged
 * @returns {boolean}
 */
const isFreshDetailsContext = ({ enteredDetailsTab, farmingUnitChanged }) =>
  Boolean(enteredDetailsTab || farmingUnitChanged);

module.exports = {
  DETAILS_TAB,
  hasValidFieldSelection,
  autoSelectAppliesHere,
  shouldAutoSelectFirstField,
  shouldRealignAutoSelection,
  isFreshDetailsContext,
};
